import { useEffect, useState } from "react";
import { api, errMsg } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent } from "@/components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogTrigger,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Plus, ArrowDownToLine, ArrowUpFromLine, Pencil, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";

const today = () => new Date().toISOString().slice(0, 10);

export default function MovementView({ type }) {
  const isInward = type === "inward";
  const btnClass = isInward ? "bg-emerald-500 hover:bg-emerald-600" : "bg-rose-500 hover:bg-rose-600";
  const qtyClass = isInward ? "text-emerald-400" : "text-rose-400";
  const [items, setItems] = useState([]);
  const [movements, setMovements] = useState([]);
  const [open, setOpen] = useState(false);
  const emptyForm = { item_id: "", quantity: "", date: today(), party: "", reference: "", inward_no: "", notes: "" };
  const [form, setForm] = useState(emptyForm);
  const [editing, setEditing] = useState(null);
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";

  const openNew = () => { setEditing(null); setForm(emptyForm); setOpen(true); };
  const openEdit = (m) => {
    setEditing(m);
    setForm({ item_id: m.item_id, quantity: String(m.quantity), date: m.date, party: m.party || "", reference: m.reference || "", inward_no: m.inward_no || "", notes: m.notes || "" });
    setOpen(true);
  };
  const del = async (m) => {
    if (!window.confirm(`Delete this ${type} entry? Stock will be adjusted.`)) return;
    try { await api.delete(`/movements/${m.id}`); toast.success("Entry deleted"); load(); loadItems(); } catch (err) { toast.error(errMsg(err)); }
  };

  const load = () => {
    api.get("/movements", { params: { type } }).then((r) => setMovements(r.data)).catch((e) => toast.error(errMsg(e)));
  };
  const loadItems = () => api.get("/items").then((r) => setItems(r.data)).catch(() => {});

  useEffect(() => {
    load();
    loadItems();
    // eslint-disable-next-line
  }, [type]);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.item_id) return toast.error("Please select an item");
    if (isInward && !form.inward_no.trim()) return toast.error("Please enter Inward No");
    try {
      if (editing) await api.put(`/movements/${editing.id}`, { ...form, type, quantity: parseFloat(form.quantity) });
      else await api.post("/movements", { ...form, type, quantity: parseFloat(form.quantity) });
      toast.success(editing ? "Entry updated" : isInward ? "Inward entry logged" : "Outward entry logged");
      setOpen(false);
      setEditing(null);
      setForm(emptyForm);
      load();
      loadItems();
    } catch (err) {
      toast.error(errMsg(err));
    }
  };

  const selectedItem = items.find((i) => i.id === form.item_id);

  return (
    <div className="space-y-6 animate-fade-up" data-testid={`${type}-page`}>
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div>
          <h1 className="text-3xl sm:text-4xl font-extrabold tracking-tight flex items-center gap-3">
            {isInward ? <ArrowDownToLine className="h-8 w-8 text-emerald-400" /> : <ArrowUpFromLine className="h-8 w-8 text-rose-400" />}
            {isInward ? "Inward Register" : "Outward Register"}
          </h1>
          <p className="text-muted-foreground mt-1">
            {isInward ? "Log items received into stock." : "Log items dispatched out of stock."}
          </p>
        </div>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button data-testid={`add-${type}-btn`} onClick={openNew} className={`${btnClass} text-white gap-2`}>
              <Plus className="h-4 w-4" /> {isInward ? "New Inward" : "New Outward"}
            </Button>
          </DialogTrigger>
          <DialogContent className="bg-card border-border">
            <DialogHeader>
              <DialogTitle>{editing ? `Edit ${isInward ? "Inward" : "Outward"} Entry` : isInward ? "New Inward Entry" : "New Outward Entry"}</DialogTitle>
            </DialogHeader>
            <form onSubmit={submit} className="space-y-4">
              {isInward && (
                <div className="space-y-2">
                  <Label>Inward No <span className="text-rose-400">*</span></Label>
                  <Input data-testid="inward-no-input" required value={form.inward_no}
                    onChange={(e) => setForm({ ...form, inward_no: e.target.value })} placeholder="e.g. IN-101" />
                </div>
              )}
              <div className="space-y-2">
                <Label>Item</Label>
                <Select value={form.item_id} onValueChange={(v) => setForm({ ...form, item_id: v })}>
                  <SelectTrigger data-testid={`${type}-item-select`}><SelectValue placeholder="Select item" /></SelectTrigger>
                  <SelectContent>
                    {items.length === 0 ? (
                      <div className="px-3 py-2 text-sm text-muted-foreground">No items. Add items first.</div>
                    ) : items.map((i) => (
                      <SelectItem key={i.id} value={i.id}>
                        {i.name} ({i.sku}) · stock: {i.current_stock}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {selectedItem && (
                  <p className="text-xs text-muted-foreground">
                    Current stock: <span className="font-mono-sku text-sky-400">{selectedItem.current_stock} {selectedItem.unit}</span>
                  </p>
                )}
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Quantity</Label>
                  <Input data-testid={`${type}-quantity-input`} type="number" step="0.01" min="0" required
                    value={form.quantity} onChange={(e) => setForm({ ...form, quantity: e.target.value })} />
                </div>
                <div className="space-y-2">
                  <Label>Date</Label>
                  <Input data-testid={`${type}-date-input`} type="date" required value={form.date}
                    onChange={(e) => setForm({ ...form, date: e.target.value })} />
                </div>
              </div>
              <div className="space-y-2">
                <Label>{isInward ? "Supplier / From" : "Destination / To"}</Label>
                <Input data-testid={`${type}-party-input`} value={form.party}
                  onChange={(e) => setForm({ ...form, party: e.target.value })} placeholder={isInward ? "Supplier name" : "Destination"} />
              </div>
              <div className="space-y-2">
                <Label>Reference</Label>
                <Input data-testid={`${type}-reference-input`} value={form.reference}
                  onChange={(e) => setForm({ ...form, reference: e.target.value })} placeholder="DC / Invoice No." />
              </div>
              <div className="space-y-2">
                <Label>Notes</Label>
                <Textarea value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} placeholder="Optional" />
              </div>
              <DialogFooter>
                <Button type="submit" data-testid={`${type}-submit-btn`} className={`${btnClass} text-white`}>
                  Save Entry
                </Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      <Card className="bg-card border-border">
        <CardContent className="p-0 overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-border text-left text-muted-foreground">
                {isInward && <th className="px-4 py-3 font-medium">Inward No</th>}
                <th className="px-4 py-3 font-medium">Date</th>
                <th className="px-4 py-3 font-medium">Item</th>
                <th className="px-4 py-3 font-medium">SKU</th>
                <th className="px-4 py-3 font-medium text-right">Qty</th>
                <th className="px-4 py-3 font-medium text-right">Balance</th>
                <th className="px-4 py-3 font-medium">{isInward ? "Supplier" : "Destination"}</th>
                <th className="px-4 py-3 font-medium">Ref</th>
                <th className="px-4 py-3 font-medium">By</th>
                {isAdmin && <th className="px-4 py-3"></th>}
              </tr>
            </thead>
            <tbody data-testid={`${type}-table-body`}>
              {movements.length === 0 ? (
                <tr><td colSpan={10} className="px-4 py-12 text-center text-muted-foreground">No {type} entries yet.</td></tr>
              ) : movements.map((m) => (
                <tr key={m.id} className="border-b border-border/50 hover:bg-white/5">
                  {isInward && <td className="px-4 py-3 font-mono-sku text-xs text-sky-300" data-testid={`inward-no-${m.id}`}>{m.inward_no || "—"}</td>}
                  <td className="px-4 py-3 font-mono-sku text-xs">{m.date}</td>
                  <td className="px-4 py-3 font-medium">{m.item_name}</td>
                  <td className="px-4 py-3 font-mono-sku text-xs text-muted-foreground">{m.item_sku}</td>
                  <td className={`px-4 py-3 text-right font-mono-sku font-semibold ${qtyClass}`}>
                    {isInward ? "+" : "-"}{m.quantity} {m.unit}
                  </td>
                  <td className="px-4 py-3 text-right font-mono-sku">{m.balance_after}</td>
                  <td className="px-4 py-3">{m.party || "—"}</td>
                  <td className="px-4 py-3 text-muted-foreground">{m.reference || "—"}</td>
                  <td className="px-4 py-3 text-muted-foreground">{m.created_by}</td>
                  {isAdmin && (
                    <td className="px-4 py-3 text-right whitespace-nowrap">
                      <Button size="icon" variant="ghost" data-testid={`${type}-edit-${m.id}`} onClick={() => openEdit(m)} className="h-8 w-8 text-sky-400 hover:bg-sky-500/10"><Pencil className="h-4 w-4" /></Button>
                      <Button size="icon" variant="ghost" data-testid={`${type}-delete-${m.id}`} onClick={() => del(m)} className="h-8 w-8 text-rose-400 hover:bg-rose-500/10"><Trash2 className="h-4 w-4" /></Button>
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </CardContent>
      </Card>
    </div>
  );
}
